import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { NotFoundPage } from '@/widgets/not-found';
import { type SettingsSection, useOrgContext, visibleSettingsSections } from '../../entities/org';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../shared/ui/breadcrumb';
import { PageLayout } from '../../shared/ui/page-layout';
import { TopBarBreadcrumb } from '../../widgets/app-shell';

type Crumb = { label: string; to?: string };

/** "Settings › …" in the top bar. The org name is not in it: the sidebar's switcher shows it. */
export function SettingsBreadcrumb({ trail }: { trail: Crumb[] }) {
  const { t } = useTranslation('shell');
  const { org } = useOrgContext();
  const crumbs: Crumb[] = [
    { label: t('nav.settings'), to: `/o/${org.slug}/settings/general` },
    ...trail,
  ];
  return (
    <TopBarBreadcrumb>
      <Breadcrumb>
        <BreadcrumbList>
          {crumbs.map((crumb, index) => (
            <Fragment key={crumb.label}>
              {index > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem>
                {index === crumbs.length - 1 || !crumb.to ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </TopBarBreadcrumb>
  );
}

/**
 * One organization settings section (`/o/:orgSlug/settings/<section>`): titled with the section, the org
 * name beneath. A section the caller has nothing to do in is the standard not-found page, like any other
 * address they cannot use; the RPCs refuse the rest regardless.
 */
export function SettingsPage({
  section,
  children,
}: {
  section: SettingsSection;
  children: ReactNode;
}) {
  const { t } = useTranslation('org');
  const scope = useOrgContext();
  const title = t(`settings.tabs.${section}`);
  useDocumentTitle(title);
  if (!visibleSettingsSections(scope).includes(section)) return <NotFoundPage />;
  return (
    <PageLayout title={title} description={scope.org.name}>
      <SettingsBreadcrumb trail={[{ label: title }]} />
      {children}
    </PageLayout>
  );
}
